// src/components/sales/pages/clients/ClientsPage.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ClientsView from "./ClientsView";
import { clientsService } from "../../../../services/clients.service";

export default function ClientsPage({ authData, logout }) {
  const { t, i18n } = useTranslation();
  void i18n;

  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [dataBlocks, setDataBlocks] = useState([]);

  // Message banner بدل alert
  const [uiMessage, setUiMessage] = useState({ type: "", text: "" });

  const setError = (text) =>
    setUiMessage({ type: "error", text: text || t("sales.clients.requestFailed") });
  const setSuccess = (text) =>
    setUiMessage({ type: "success", text: text || t("sales.clients.done") });
  const clearMessage = () => setUiMessage({ type: "", text: "" });

  const unwrapAnyArray = (res) => {
    if (Array.isArray(res)) return res;

    const d1 = res?.data ?? res?.result ?? res?.items ?? res;
    if (Array.isArray(d1)) return d1;

    if (Array.isArray(d1?.data)) return d1.data;
    if (Array.isArray(d1?.dataBlocks)) return d1.dataBlocks;

    return [];
  };

  const isForbidden = (e) => String(e?.message || "").toLowerCase().startsWith("http 403");
  const isNotFound = (e) => String(e?.message || "").toLowerCase().startsWith("http 404");

  const loadDataBlocks = async () => {
    const res = await clientsService.dataBlocks();
    const list = unwrapAnyArray(res);
    setDataBlocks(list);
    return list;
  };

  const loadClients = async () => {
    const res = await clientsService.list({});
    const list = unwrapAnyArray(res);
    setClients(list);
    return list;
  };

  const refreshAll = async () => {
    setLoading(true);
    clearMessage();
    try {
      await Promise.all([loadDataBlocks(), loadClients()]);
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      setError(e?.message || t("sales.clients.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createClient = async (payload) => {
    setLoading(true);
    clearMessage();
    try {
      await clientsService.create(payload);
      await loadClients();
      setSuccess(t("sales.clients.createdSuccess"));
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return false;
      }
      setError(e?.message || t("sales.clients.createFailed"));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateClient = async (id, payload) => {
    const n = Number(id);
    if (!Number.isFinite(n)) {
      setError(t("sales.clients.invalidClientId"));
      return false;
    }

    setLoading(true);
    clearMessage();
    try {
      await clientsService.update(n, payload);
      await loadClients();
      setSuccess(t("sales.clients.updatedSuccess"));
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return false;
      }
      setError(e?.message || t("sales.clients.updateFailed"));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteClient = async (id) => {
    const n = Number(id);
    if (!Number.isFinite(n)) {
      setError(t("sales.clients.invalidClientId"));
      return;
    }

    const ok = window.confirm(t("sales.clients.confirmDelete"));
    if (!ok) return;

    setLoading(true);
    clearMessage();

    try {
      await clientsService.remove(n);
      await loadClients();
      setSuccess(t("sales.clients.deletedSuccess"));
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }

      if (isForbidden(e)) {
        setError(t("sales.clients.deleteForbidden"));
      } else if (isNotFound(e)) {
        setError(t("sales.clients.deleteNotFound"));
        await loadClients().catch(() => {});
      } else {
        setError(e?.message || t("sales.clients.deleteFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ClientsView
      authData={authData}
      logout={logout}
      clients={clients}
      dataBlocks={dataBlocks}
      loading={loading}
      message={uiMessage}
      onClearMessage={clearMessage}
      onRefresh={refreshAll}
      onCreate={createClient}
      onUpdate={updateClient}
      onDelete={deleteClient}
    />
  );
}
